import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'

import HttpStatus from 'components/Common/HttpStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ErrorPage from 'components/Pages/ErrorPage'
import {
  fetchLandingPage,
} from 'actions/ContentActions'
import OfferList, { OfferListV2Item } from 'components/OfferList/OfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Content from 'components/Pages/LandingPage/Content'
import { mediaQueryUp } from 'components/utils/breakpoint'
import MagazineArticles from './MagazineArticles'
import HowItWorksBanner from 'components/Pages/FrontPage/HowItWorksBanner'
import config from 'constants/config'
import Hero from './Hero'
import { RouteComponentProps } from 'react-router'
import { getContentPageKey } from 'lib/url/contentUtils'
import { PAGE_NAME_HOMES_AND_VILLAS, TYPE_OF_PAGE_HOLIDAY } from 'constants/landingPage'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferRecommendations from 'components/Common/OfferRecommendations'
import LocationSeededYmalList from 'components/Recommendations/LocationSeeded/LocationSeededYmalList'
import LocationLereOfferList from 'components/Recommendations/LocationSeeded/LocationLereOfferList'
import { getListName } from 'analytics/enhanced-ecommerce'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { FLASH_AND_TAO_OFFERS_TYPES } from 'constants/offerList'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const CenteredSpacing = styled.div`
  text-align: center;
`

const ResponsiveSpacing = styled.div`
  margin-top: ${rem(40)};
  margin-bottom: ${rem(40)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(65)};
    margin-bottom: ${rem(65)};
  }
`

interface Props extends RouteComponentProps<{ slug: string }> {
  currency: string;
  typeOfPage: string;
  landingPage: App.ContentHolidayPage | App.ContentDestinationPage;
  regionCode: string;
  error?: any;
  lereEnabled?: boolean;
  lereLePlaceId?: string;
  lereLePlaceName?: string;
}

const ANALYTICS_DESTINATION_LANDING_PAGE_TAG = 'DLP'

function LandingPage(props: Props) {
  const { error, landingPage, typeOfPage, regionCode, match, lereEnabled = false, lereLePlaceId, lereLePlaceName } = props
  const pageSlug = match.params.slug

  const {
    title,
    subTitle,
    metaDescription,
    filters,
    search,
    image,
    content,
    slug,
    hideSignUpOverlay,
    backgroundColour,
  } = landingPage ?? {}

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchLandingPage(pageSlug, typeOfPage))
  }, [dispatch, pageSlug, typeOfPage])

  const isHolidayHomesPage = typeOfPage === TYPE_OF_PAGE_HOLIDAY && pageSlug === PAGE_NAME_HOMES_AND_VILLAS
  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Cruise Page Offer List'),
    { ias: isHolidayHomesPage ? { list: 'holiday-homes' } : undefined },
  )

  const invertedFilters = useMemo<App.OfferListFilters>(() => {
    if (filters) {
      return {
        holidayTypes: 'holidayTypes' in filters ? filters.holidayTypes.map(type => `!${type}`) : undefined,
        locations: 'locations' in filters ? filters.locations.map(type => `!${type}`) : undefined,
      }
    }
    return {}
  }, [filters])

  if (error) {
    if (error.status === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <ErrorPage error={error} />
    }
  }

  if (!landingPage) {
    return <LoadingIndicator />
  }

  const offerListFilters: App.OfferListFilters = {
    ...filters,
    offerTypes: FLASH_AND_TAO_OFFERS_TYPES,
    frontPageVisibleOnly: true,
  }

  const metaData = {
    title: config.title,
    description: metaDescription || `Make your dream ${title} holiday a reality! Discover the best that ${title} has on offer and save with Luxury Escapes holiday packages & accommodation deals.`,
  }

  const headTitle = `${title} - ${config.title} ${regionCode}`
  const magazineUrl = `${config.MAGAZINE_PORTAL}/tag/${slug}`

  const canShowLereLocationBasedOffers = lereEnabled && !!lereLePlaceId

  const offerListExtas: Array<OfferListV2Item> = [{
    id: 'message',
    position: 'start',
    element: (
      <ResponsiveSpacing>
        <LayoutContainer size="sm">
          <VerticalSpacer gap={20}>
            <VerticalSpacer gap={12}>
              <Heading variant="heading3" align="center">
                We are currently sold out of our {title} escapes!
              </Heading>
              <BodyText variant="large" align="center">
                Our travel experts are working hard on curating new {title} offers.
                In the meantime check out our available offers in other exciting destinations.
              </BodyText>
            </VerticalSpacer>
            <CenteredSpacing>
              <TextButton kind="primary" to="/">
                Browse all available offers
              </TextButton>
            </CenteredSpacing>
          </VerticalSpacer>
        </LayoutContainer>
      </ResponsiveSpacing>
    ),
    show: 'hasNoOffers',
  }, {
    id: 'howitworks',
    position: 3,
    element: <HowItWorksBanner />,
  }]

  return (
    <Pane type={backgroundColour === 'white' ? undefined : 'clear'}>
      <VerticalSpacer gap={20}>
        <Hero
          title={`${title} Holiday Packages`}
          subTitle={subTitle}
          image={image}
          headTitle={headTitle}
          typeOfPage={typeOfPage}
          metaData={metaData}
          version="landing"
          hideSignUpOverlay={hideSignUpOverlay}
          back={{
            link: '/',
            title: 'View all offers',
          }}
        />

        <LayoutContainer>
          {canShowLereLocationBasedOffers && <LocationLereOfferList
            extras={offerListExtas}
            analyticPageSource={ANALYTICS_DESTINATION_LANDING_PAGE_TAG}
            lereLePlaceId={lereLePlaceId}
          />}
          {!canShowLereLocationBasedOffers &&
            <OfferListEventsProvider tracking={tracking}>
              <OfferList
                filters={offerListFilters}
                interstitials={offerListExtas}
              />
            </OfferListEventsProvider>
          }
        </LayoutContainer>

        {(content.video || content.description) &&
          <Content
            title={title}
            header={content.title}
            videoUrl={content.video}
            description={content.description}
          />}
      </VerticalSpacer>

      {search &&
        <MagazineArticles tags={[search]} viewMoreUrl={magazineUrl} axis="vertical" title="From Luxury Escapes Magazine" />
      }

      <Pane axis="vertical">
        <PaneBody size="xl-y">
          {canShowLereLocationBasedOffers && <LocationSeededYmalList
            destinationId={lereLePlaceId}
            placeName={lereLePlaceName!}
            analyticPageSource={ANALYTICS_DESTINATION_LANDING_PAGE_TAG}
          />}
          {!canShowLereLocationBasedOffers && <OfferRecommendations
            filters={invertedFilters}
            trackingDescription="Landing Page"
          />}
        </PaneBody>
      </Pane>
    </Pane>
  )
}

type MappedProps = Pick<Props, 'currency' | 'landingPage' | 'error' | 'regionCode'>

function mapStateToProps(state: App.State, ownProps: Omit<Props, keyof MappedProps>): MappedProps {
  const key = getContentPageKey(ownProps.match.params.slug, ownProps.typeOfPage)

  return {
    currency: state.geo.currentCurrency,
    landingPage: state.content.landingPages[key] as any,
    error: state.content.landingPageErrors[key],
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(LandingPage)
